"use client";

import { dec } from "@/lib/num";
import { useEffect, useState, type RefObject } from "react";
import { useDropDirection } from "@/lib/use-drop-direction";
import type { IconType } from "@/lib/module-icons";
import { MessageSquare, TriangleAlert, Clock, KeyRound } from "lucide-react";
import { IconBell, IconCheck} from "@/components/ui/icons";
import Link from "next/link";
import { api } from "@/lib/api";
import type { JobsHealth, WbReviewSummary } from "@/lib/types";
import { useAuth } from "@/components/auth-provider";
import { hasPermission } from "@/lib/access";
import { glassPillStyle } from "@/lib/theme";
import { useTheme } from "@/components/theme-provider";

type NotificationItem = {
  id: string;
  icon: IconType;
  iconColor: string;
  title: string;
  description: string;
  href?: string;
  count?: number;
};

type Props = {
  collapsed: boolean;
  /** Вид для панели в строке заголовка: значок 36×36, список падает ВНИЗ и
   *  прижат к правому краю (Максим, 28.08.2026 — панель переехала в шапку). */
  inline?: boolean;
  // Участвует ли кнопка в общей переезжающей подсветке нижней группы рельса
  // (в свёрнутом виде — нет: там пункты кружками, пилюля им не подходит).
  mh?: boolean;
  // open — управляемое состояние: живёт в AppShell (переживает ремаунт сайдбара
  // и держит бар развёрнутым через deskExpanded). Внутренний useState тут сбрасывался
  // бы при каждом ре-рендере AppShell (SidebarContent — вложенный компонент).
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Ref-обёртка приходит из AppShell (раздельные desktop/mobile), чтобы
  // useOutsideClick не ломался из-за двойного монтажа SidebarContent.
  containerRef?: RefObject<HTMLDivElement | null>;
};

export function NotificationsButton({ collapsed, open = false, onOpenChange, containerRef, mh = false, inline = false }: Props) {
  const { mode } = useTheme();
  // Направление и высоту считает общий хук — тот же, что у меню профиля:
  // соседние панели рельса должны вести себя одинаково.
  const { anchorRef, dropDown, maxHeight: listMax } = useDropDirection(open);
  const btnRef = anchorRef as React.RefObject<HTMLButtonElement | null>;
  const { user } = useAuth();
  const [reviewSummary, setReviewSummary] = useState<WbReviewSummary | null>(null);
  // Здоровье синков — только администратору: рядовому сотруднику строка
  // «Логистика МП по габаритам падает» ничего не говорит и починить он её не может.
  const isAdmin = user?.role === "platform_admin";
  const [jobs, setJobs] = useState<JobsHealth | null>(null);
  // Заявки на доступ с экрана входа. Тоже только администратору: он их и
  // разбирает (доступ в портале выдают владелец с администратором).
  const [access, setAccess] = useState<{ new: number; names: string[] } | null>(null);
  const setOpen = (v: boolean) => onOpenChange?.(v);

  // Сводка по отзывам — только тем, кому открыт модуль. Раньше запрос уходил у
  // всех, каждую минуту, и у рядового сотрудника отвечал 403: ошибка глушилась
  // .catch, но копилась в консоли и в логах бэкенда (28.08.2026).
  const canSeeReviews = hasPermission(user, "reviews", "view");
  useEffect(() => {
    if (!canSeeReviews) return;
    let cancelled = false;
    const load = () => {
      api.getWbSummary().then((s) => { if (!cancelled) setReviewSummary(s); }).catch(() => {});
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canSeeReviews]);

  // ⚠️ Заявку кладёт человек, которому портал НЕДОСТУПЕН: он не может ни
  // написать в чат портала, ни поставить задачу. Кроме колокольчика сообщить
  // о нём нечем, поэтому опрашиваем чаще синков — раз в минуту, как отзывы.
  useEffect(() => {
    if (!isAdmin) { setAccess(null); return; }
    let cancelled = false;
    const load = () => {
      api.countAccessRequests().then((c) => { if (!cancelled) setAccess({ new: c.new, names: c.names ?? [] }); }).catch(() => {});
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) { setJobs(null); return; }
    let cancelled = false;
    const load = () => {
      api.jobsHealth().then((h) => { if (!cancelled) setJobs(h); }).catch(() => {});
    };
    load();
    // Синки живут часами, поэтому раз в пять минут — достаточно.
    const interval = setInterval(load, 300_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAdmin]);


  const items: NotificationItem[] = [];
  if (access && access.new > 0) {
    const shown = access.names.slice(0, 3);
    const rest = access.new - shown.length;
    items.push({
      id: "access-requests",
      icon: KeyRound,
      iconColor: "var(--warning)",
      title: access.new === 1 ? "Заявка на доступ" : "Заявки на доступ",
      // Имена, а не одно число: по ним сразу видно, ждать этого человека или нет.
      description: `${shown.join(", ")}${rest > 0 ? ` и ещё ${rest}` : ""} — не могут войти`,
      // ⚠️ Ведёт на «Команда» → «Сотрудники», где заявки и живут. Раньше вело на
      // отдельный экран учёток: человек из колокольчика попадал на второй список
      // тех же людей (Максим, 31.08.2026).
      href: "/team?view=requests",
      count: access.new,
    });
  }
  if (reviewSummary && reviewSummary.pendingReviews > 0) {
    items.push({
      id: "wb-reviews-pending",
      icon: MessageSquare,
      iconColor: "var(--warning)",
      title: "Отзывы без ответа",
      description: `${reviewSummary.pendingReviews.toLocaleString("ru-RU")} требуют ответа · средний рейтинг ★ ${dec(reviewSummary.averageRating, 2)}`,
      href: "/departments/sales",
      count: reviewSummary.pendingReviews,
    });
  }

  /** Подсказка, что делать: техническая ошибка сама по себе ничего не говорит
   *  тому, кто её читает в колокольчике. */
  const hint = (note: string) => {
    const n = (note || "").trim();
    if (!n) return "";
    // Если задача уже написала причину по-человечески — показываем ЕЁ, а не свою
    // выдумку: она точнее. Технический текст (JSON, код ответа, стек) прячем.
    // Техническим считаем и внутреннюю сводку задачи — «кабинетов: 0, с ошибкой: 2»:
    // она написана для журнала, а не для читателя уведомления. Признак — строчная
    // буква в начале вместе с двоеточием.
    const technical = n.includes("{") || /\b[45]\d{2}\b/.test(n)
      || /error|exception|traceback/i.test(n)
      || (n.includes(":") && n[0] === n[0].toLowerCase() && /[а-яa-z]/.test(n[0]));
    if (!technical) {
      // Панель узкая (248 px): длинная причина занимает пол-экрана и всё равно
      // обрывается. Берём главное — до первого тире или точки, не длиннее 52
      // символов; подробности человек увидит на экране задач по клику.
      const head = n.split(/ — |\. /)[0].trim();
      const short = head.length > 52 ? `${head.slice(0, 50).trimEnd()}…` : head;
      return ` · ${short}`;
    }
    const l = n.toLowerCase();
    if (l.includes("invalid_grant") || l.includes("token") || l.includes("токен")) {
      return ", нужно переподключить кабинет";
    }
    if (l.includes("отказ источника")) return ", источник данных отвечает неполно — ждём";
    return "";
  };

  /** «15 ч назад» человеку понятно, «0 ч назад» — нет. */
  const ago = (h: number | null) => {
    if (h == null) return "";
    if (h < 1) return "только что";
    if (h < 24) return `${dec(h, 0)} ч назад`;
    const d = Math.round(h / 24);
    return `${d} ${d === 1 ? "день" : d < 5 ? "дня" : "дней"} назад`;
  };

  if (jobs?.failing.length) {
    items.push({
      id: "jobs-failing",
      icon: TriangleAlert,
      iconColor: "var(--danger)",
      title: jobs.failing.length === 1 ? "Фоновая задача не отработала" : "Фоновые задачи не отработали",
      description: jobs.failing
        .map((j) => `• ${j.title}${j.hoursAgo != null ? ` — ${ago(j.hoursAgo)}` : ""}${hint(j.note)}`)
        .join("\n"),
      href: "/settings/modules#sync",
      count: jobs.failing.length,
    });
  }
  if (jobs?.stale.length) {
    items.push({
      id: "jobs-stale",
      icon: Clock,
      iconColor: "var(--warning)",
      title: "Синхронизация давно молчит",
      description: jobs.stale
        .map((j) => `• ${j.title} — ${ago(j.hoursAgo)}`)
        .join("\n"),
      href: "/settings/modules#sync",
      count: jobs.stale.length,
    });
  }

  const totalCount = items.reduce((s, x) => s + (x.count ?? 1), 0);
  const hasUnread = totalCount > 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        data-tooltip="Уведомления"
        // Пункт общей переезжающей подсветки нижней группы. Пока панель открыта,
        // пилюля на кнопку не заезжает: у неё в этот момент свой вид.
        data-mh={mh ? "item" : undefined}
        data-mh-skip={open ? "1" : undefined}
        className={
          inline
            // `glass-flat` — отказ от общей тени кнопок (globals.css вешает её на
            // всё, кроме помеченных): на значке панели она читалась коробкой.
            // В режиме рельса класс уже стоял, в панели его не хватало.
            ? "glass-flat relative inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] transition"
            : `glass-flat group flex w-full items-center gap-1 transition ${collapsed ? "" : "h-11 rounded-[var(--radius-md)] pr-3 glass-nav"}`
        }
      >
        {inline && (
          <>
            <IconBell size={17} className={hasUnread ? "text-[var(--warning)]" : "text-[var(--textMuted)]"} />
            {hasUnread && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--danger)] px-1 text-[11px] font-bold leading-none text-white">
                {totalCount > 99 ? "99+" : totalCount}
              </span>
            )}
          </>
        )}
        {/* Значок в центрирующем слоте (как у пунктов рельса): одинаковый x
            свёрнуто/развёрнуто. Свёрнуто — в кружке-индикаторе; развёрнуто — голый значок. */}
        <span className={inline ? "hidden" : "flex w-[46px] shrink-0 items-center justify-center"}>
          {collapsed ? (
            <span className={`relative flex h-12 w-12 items-center justify-center rounded-full border-2 transition-colors glass-nav ${hasUnread ? "border-[color-mix(in_srgb,var(--warning)_45%,transparent)]" : "border-[color-mix(in_srgb,var(--textMuted)_38%,transparent)]"}`}>
              <IconBell size={22} className={hasUnread ? "text-[var(--warning)]" : "text-[var(--textMuted)]"} />
              {hasUnread && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--danger)] px-1 text-[11px] font-bold text-white leading-none">
                  {totalCount > 99 ? "99+" : totalCount}
                </span>
              )}
            </span>
          ) : (
            <span className="relative flex items-center justify-center">
              <IconBell size={22} className={hasUnread ? "text-[var(--warning)]" : "text-[var(--textMuted)]"} />
              {hasUnread && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--danger)] px-1 text-[11px] font-bold text-white leading-none">
                  {totalCount > 99 ? "99+" : totalCount}
                </span>
              )}
            </span>
          )}
        </span>
        {!collapsed && !inline && (
          <div className="min-w-0 flex-1 text-left">
            <div className="text-[12px] font-semibold leading-tight text-[var(--text)]">Уведомления</div>
            <div className="text-[11px] text-[var(--textMuted)]">
              {hasUnread ? `${totalCount} новых` : "Всё спокойно"}
            </div>
          </div>
        )}
      </button>

      {open && (
        // Развёрнутый рельс — «пилюля», приколотая к низу и вырастающая ВВЕРХ
        // (нижняя строка ложится поверх свёрнутой кнопки, как в меню профиля).
        // Свёрнутый — панель ВЫЕЗЖАЕТ ВПРАВО (как отделы/профиль), бар не разъезжается;
        // зазор закрыт паддингом обёртки, иначе курсор в щели роняет hover.
        <div className={inline ? "absolute right-0 top-[calc(100%+8px)] z-fab" : collapsed ? `absolute ${dropDown ? "top-0" : "bottom-0"} left-full z-fab pl-2` : "contents"}>
        <div
          className={`flex flex-col rounded-[var(--radius-lg)] overflow-hidden z-50 p-1 ${inline ? "yuc-unfurl-tr w-[300px]" : collapsed ? "yuc-unfurl-bl w-[248px]" : `yuc-grow-up absolute inset-x-0 ${dropDown ? "top-0" : "bottom-0"}`}`}
          style={{ ...glassPillStyle(mode === "dark"), background: "var(--surfaceElevated)" }}
        >
          {/* tight-вариант: в узкой панели резерв места под полосу (scrollbar-gutter:
              stable) читался как пустая колонка справа. */}
          <div className="overflow-auto scrollbar-autohide-tight" style={{ maxHeight: listMax }}>
            {items.length === 0 ? (
              <div className="flex items-center justify-center gap-1.5 px-3 py-5 text-center text-[11px] text-[var(--textMuted)]">
                Нет новых событий
                <IconCheck size={14} />
              </div>
            ) : (
              <div className="flex flex-col gap-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const inner = (
                    <div className="group flex items-start gap-3 rounded-[var(--radius-lg)] px-2.5 py-2 transition glass-nav">
                      <div
                        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
                        style={{ backgroundColor: `color-mix(in srgb, ${item.iconColor} 15%, transparent)`, color: item.iconColor }}
                      >
                        <Icon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-[var(--text)]">{item.title}</div>
                        <div className="mt-0.5 whitespace-pre-line text-[11px] text-[var(--textMuted)] leading-snug">
                          {item.description}
                        </div>
                      </div>
                    </div>
                  );
                  return item.href ? (
                    <Link key={item.id} href={item.href} onClick={() => setOpen(false)} className="block">
                      {inner}
                    </Link>
                  ) : (
                    <div key={item.id}>{inner}</div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mx-1 my-1 h-px shrink-0" style={{ background: "color-mix(in srgb, var(--text) 10%, transparent)" }} />

          {/* Нижняя строка «пилюли» — сама кнопка уведомлений; клик сворачивает */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="group flex shrink-0 items-center gap-2.5 rounded-full px-2.5 py-1.5 text-left transition glass-nav"
          >
            <IconBell size={16} className={hasUnread ? "text-[var(--warning)]" : "text-[var(--textMuted)]"} />
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold leading-tight text-[var(--text)]">Уведомления</div>
              <div className="text-[11px] text-[var(--textMuted)]">
                {hasUnread ? `${totalCount} новых` : "Всё спокойно"}
              </div>
            </div>
          </button>
        </div>
        </div>
      )}
    </div>
  );
}
